import { FC, useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { ProgressStudentsGrowthAndEngagementQuery } from '../../../graphQLOperations/__generated__/ProgressStudentsGrowthAndEngagementQuery.graphql';
import { getStudentGrowthAndEngagement } from '../../../graphQLOperations/Progress';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import ProgressColumnName from './ProgressColumnName';
import { useAppSelector } from '../../../redux/hooks';
import { selectedReportsFilter } from '../../../redux/reducers/filterReportsSlice';
import { selectUser } from '../../../redux/reducers/userSlice';
import { formatDateWithTimezone } from '../../../businessLogicService/dateFormatting';
import { getColorForValue } from '../../../businessLogicService/getColorsForProgressTableChip';
import { Row } from 'primereact/row';
import { ColumnGroup } from 'primereact/columngroup';
import ProgressColumnAssessment from './ProgressColumnAssessment';
import { CustomBranding, mapCustomBranding } from '../../../models/User';
import { StudentNameLink } from '../../common/StudentNameLink/StudentNameLink';
import { MaskedStudentData } from '../../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../../analytics/dataMasking';

interface Props {
  growthQueryRef: PreloadedQuery<ProgressStudentsGrowthAndEngagementQuery>;
}

enum GrowthFieldType {
  SKILLS_PASSED,
  ROADMAP_SKILLS_REMAINING,
  SKILLS_ATTEMPTED,
}

interface GrowthTableValues {
  studentFirstName: string;
  studentLastName: string;
  studentId: string;
  studentPublicUserId: string;
  currentRoadmap: string;
  skillsDemonstratedOnDiagnostic: number;
  skillsPassedSinceDiagnostic: number;
  roadmapSkillsRemaining: string;
  skillsAttemptedButNotPassed: number;
  lastPassedSkill: string;
  assessmentsTakenSinceLastPass: number;
  totalAssessmentsTaken: number;
  totalRoadmapSkills: number;
}
const GrowthAndEngagementTable: FC<Props> = ({ growthQueryRef }) => {
  const data = usePreloadedQuery(getStudentGrowthAndEngagement, growthQueryRef);
  const [values, setValues] = useState<GrowthTableValues[]>([]);
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);
  const userRedux = useAppSelector(selectUser);
  const miamiBrand =
    userRedux?.network?.customBranding &&
    mapCustomBranding[userRedux?.network?.customBranding] === CustomBranding.MIAMI;
  const [minMaxValues, setMinMaxValues] = useState({
    minSkillsPassed: Infinity,
    maxSkillsPassed: -Infinity,
    minSkillsAttempted: Infinity,
    maxSkillsAttempted: -Infinity,
  });

  useEffect(() => {
    let minSkillsPassed = Infinity;
    let maxSkillsPassed = -Infinity;
    let minSkillsAttempted = Infinity;
    let maxSkillsAttempted = -Infinity;

    const valuesData = data.studentProgressReport.map(el => {
      minSkillsPassed = Math.min(minSkillsPassed, el.skillsPassedSinceDiagnostic);
      maxSkillsPassed = Math.max(maxSkillsPassed, el.skillsPassedSinceDiagnostic);
      minSkillsAttempted = Math.min(minSkillsAttempted, el.skillsAttemptedButNotPassed);
      maxSkillsAttempted = Math.max(maxSkillsAttempted, el.skillsAttemptedButNotPassed);
      return {
        studentLastName: el.student.lastName ?? '',
        studentFirstName: el.student.firstName ?? '',
        studentId: el.student.id,
        studentPublicUserId: el.student.publicUserId ?? '',
        currentRoadmap: String(el.currentRoadmapId),
        currentRoadmapName: String(el.currentRoadmapName),
        skillsDemonstratedOnDiagnostic: el.skillsDemonstratedOnDiagnostic,
        skillsPassedSinceDiagnostic: el.skillsPassedSinceDiagnostic,
        roadmapSkillsRemaining: `${el.roadmapSkillsRemaining}/${el.totalRoadmapSkills}`,
        skillsAttemptedButNotPassed: el.skillsAttemptedButNotPassed,
        lastPassedSkill: formatDateWithTimezone(
          el?.lastPassedSkill || '',
          userRedux?.activeTimeZone || '',
          'yyyy-MM-dd',
        ),
        assessmentsTakenSinceLastPass: el.assessmentsTakenSinceLastPass,
        totalAssessmentsTaken: el.totalAssessmentsTaken,
        totalRoadmapSkills: el.totalRoadmapSkills,
      };
    });
    setMinMaxValues({
      minSkillsPassed,
      maxSkillsPassed,
      minSkillsAttempted,
      maxSkillsAttempted,
    });

    setValues(valuesData);
  }, [data]);

  const commonField = (field: string | number) => {
    return (
      <div className="flex justify-content-center" style={{ fontFamily: 'Poppins' }}>
        <span>{field}</span>
      </div>
    );
  };

  const chipTemplate = (data: GrowthTableValues, field: GrowthFieldType) => {
    let colorChipValue: { backgroundColor: string; color: string } = { backgroundColor: '', color: '' };

    if (field === GrowthFieldType.SKILLS_PASSED) {
      const result = getColorForValue(
        data.skillsPassedSinceDiagnostic,
        minMaxValues.minSkillsPassed,
        minMaxValues.maxSkillsPassed,
        '#0081581F',
        '#008158',
        'green',
      );
      colorChipValue = result as { backgroundColor: string; color: string };
    }
    if (field === GrowthFieldType.SKILLS_ATTEMPTED) {
      const result = getColorForValue(
        data.skillsAttemptedButNotPassed,
        minMaxValues.minSkillsAttempted,
        minMaxValues.maxSkillsAttempted,
        '#FDB8141F',
        '#FDB814',
        'yellow',
      );
      colorChipValue = result as { backgroundColor: string; color: string };
    }
    if (field === GrowthFieldType.ROADMAP_SKILLS_REMAINING) {
      const values = data.roadmapSkillsRemaining.split('/');
      const result = getColorForValue(
        parseInt(values[0]) === 0 ? 0 : (parseInt(values[0]) / parseInt(values[1])) * 100,
        0,
        100,
        '#9C9B9B1F',
        '#9C9B9B',
        'grey',
      );
      colorChipValue = result as { backgroundColor: string; color: string };
    }

    return (
      <div className="flex justify-content-center">
        <span
          className="nc_progress-growth-table-chip"
          style={{
            backgroundColor: `${colorChipValue.backgroundColor}`,
            color: `${colorChipValue.color}`,
            width: field === GrowthFieldType.ROADMAP_SKILLS_REMAINING ? '88px' : '',
          }}
        >
          <span
            style={{
              width: field === GrowthFieldType.ROADMAP_SKILLS_REMAINING ? '48px' : '20px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            {field === GrowthFieldType.SKILLS_PASSED && data.skillsPassedSinceDiagnostic}
            {field === GrowthFieldType.ROADMAP_SKILLS_REMAINING && data.roadmapSkillsRemaining}
            {field === GrowthFieldType.SKILLS_ATTEMPTED && data.skillsAttemptedButNotPassed}
          </span>
        </span>
      </div>
    );
  };

  const headerColumnGroupTemplate = (
    <ColumnGroup>
      <Row>
        <Column colSpan={2} />
        <Column colSpan={3} header="Growth" style={{ borderBottom: '3px solid #0A9AF8', textTransform: 'uppercase' }} />
        <Column
          colSpan={4}
          header="Engagement"
          style={{ borderBottom: '3px solid #02D397', textTransform: 'uppercase' }}
        />
      </Row>
      <Row>
        <Column frozen field="studentFirstName" header="First Name" sortable />
        <Column frozen field="studentLastName" header="Last Name" sortable />
        <Column field="skillsDemonstratedOnDiagnostic" header="Skills Demonstrated on Diagnostic" sortable />
        <Column field="skillsPassedSinceDiagnostic" header="Skills Passed Since Diagnostic" sortable />
        <Column field="roadmapSkillsRemaining" header="Roadmap Skills Remaining" sortable />
        <Column field="skillsAttemptedButNotPassed" header="Skills Attempted But Not Passed" sortable />
        <Column
          field="lastPassedSkill"
          header={miamiBrand ? 'Date of Last Passed Skill' : 'Date of Last Passed Assessment'}
          sortable
        />
        <Column
          field="assessmentsTakenSinceLastPass"
          header={miamiBrand ? 'Skill Challenges Since Last Pass' : 'Assessments Taken Since Last Pass'}
          sortable
        />
        <Column
          field="totalAssessmentsTaken"
          header={miamiBrand ? 'Total Skill Challenges Taken' : 'Total Assessments Taken'}
          sortable
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <DataTable
      sortField="studentLastName"
      className="nc_progress-table"
      sortOrder={1}
      stripedRows
      totalRecords={values.length}
      rows={10}
      rowsPerPageOptions={[5, 10, 25]}
      paginator
      paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      value={values}
      headerColumnGroup={headerColumnGroupTemplate}
    >
      <Column
        frozen
        field="studentFirstName"
        header="First Name"
        style={{ width: '180px' }}
        body={(data: GrowthTableValues) => (
          <div style={{ fontFamily: 'Poppins' }}>
            <StudentNameLink studentId={data.studentId} classIds={reportsFilterRedux.classes.map(cl => cl.id)}>
              <MaskedStudentData student={{ publicUserId: data.studentPublicUserId }} type={PiiTypes.FirstName}>
                {data.studentFirstName}
              </MaskedStudentData>
            </StudentNameLink>
          </div>
        )}
      />
      <Column
        frozen
        field="studentLastName"
        header="Last Name"
        style={{ width: '180px' }}
        body={(data: GrowthTableValues) => {
          return <ProgressColumnName studentData={data} filterRoadmapId={reportsFilterRedux.roadmaps?.id as string} />;
        }}
      />
      <Column
        field="skillsDemonstratedOnDiagnostic"
        header="Skills Demonstrated on Diagnostic"
        body={data => commonField(data.skillsDemonstratedOnDiagnostic)}
      />
      <Column
        field="skillsPassedSinceDiagnostic"
        header="Skills Passed Since Diagnostic"
        body={data => chipTemplate(data, GrowthFieldType.SKILLS_PASSED)}
      />
      <Column
        field="roadmapSkillsRemaining"
        header="Roadmap Skills Remaining"
        body={data => chipTemplate(data, GrowthFieldType.ROADMAP_SKILLS_REMAINING)}
      />
      <Column
        field="skillsAttemptedButNotPassed"
        header="Skills Attempted But Not Passed"
        body={data => chipTemplate(data, GrowthFieldType.SKILLS_ATTEMPTED)}
      />
      <Column
        field="lastPassedSkill"
        header={miamiBrand ? 'Date of Last Passed Skill' : 'Date of Last Passed Assessment'}
        body={data => commonField(data.lastPassedSkill)}
      />
      <Column
        field="assessmentsTakenSinceLastPass"
        header={miamiBrand ? 'Skill Challenges Since Last Pass' : 'Assessments Taken Since Last Pass'}
        body={data => {
          return <ProgressColumnAssessment data={data} />;
        }}
      />
      <Column
        field="totalAssessmentsTaken"
        header={miamiBrand ? 'Total Skill Challenges Taken' : 'Total Assessments Taken'}
        body={data => commonField(data.totalAssessmentsTaken)}
      />
    </DataTable>
  );
};

export default GrowthAndEngagementTable;
